/**
 * Affichage paiement RDV — lit uniquement le contrat API (paymentQuote / champs plats).
 * Aucun recalcul métier.
 */

export function formatEuro(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(2).replace('.', ',')} €`
}

export function getRdvPaymentView(commande) {
  if (!commande || (commande.type !== 'RENDEZ_VOUS' && !String(commande.numero || '').startsWith('RDV-'))) {
    return null
  }

  const quote = commande.paymentQuote || {}
  const mode = String(
    quote.collectionMode || commande.paymentCollectionMode || 'NONE'
  ).toUpperCase()
  const total = Number(
    quote.totalAmount ?? commande.prixFinal ?? commande.total ?? 0
  )
  const amountPaid = Number(quote.amountPaid ?? commande.amountPaid ?? 0)
  const amountDueNow = Number(quote.amountDueNow ?? commande.amountDueNow ?? 0)
  const amountDueLater = Number(
    quote.amountDueLater ?? commande.amountDueLater ?? Math.max(0, total - amountPaid)
  )
  const paymentStatus = String(
    quote.paymentStatus || commande.paymentStatus || ''
  ).toUpperCase()
  const modePaiement = String(
    commande.modePaiement || quote.modePaiement || ''
  ).toLowerCase()

  let modeLabel = 'Non spécifié'
  let summaryLabel = ''
  let detailLines = []

  if (paymentStatus === 'REFUNDED') {
    modeLabel = mode === 'DEPOSIT_FIXED' ? 'Acompte' : mode === 'FULL' ? 'Paiement total' : modeLabel
    summaryLabel = 'Remboursé'
    detailLines = [`Remboursé — encaissé actuel : ${formatEuro(amountPaid)}`]
  } else if (mode === 'DEPOSIT_FIXED') {
    modeLabel = 'Arrhes (carte bancaire)'
    summaryLabel =
      amountPaid > 0 ? 'Arrhes payées' : paymentStatus === 'AWAITING_PAYMENT' ? 'Arrhes à régler' : 'Arrhes'
    detailLines = [
      amountPaid > 0
        ? `Arrhes payées en ligne : ${formatEuro(amountPaid)}`
        : `Arrhes à payer en ligne : ${formatEuro(amountDueNow > 0 ? amountDueNow : amountPaid)}`,
      `Reste à payer : ${formatEuro(amountDueLater)}`,
    ]
  } else if (mode === 'FULL') {
    modeLabel = 'Carte bancaire (paiement intégral)'
    summaryLabel = amountPaid >= total && total > 0 ? 'Payé intégralement' : 'Paiement en ligne'
    detailLines = [
      `Paiement : Carte bancaire`,
      amountPaid > 0
        ? `Payé en ligne : ${formatEuro(amountPaid)}`
        : `À payer en ligne : ${formatEuro(amountDueNow > 0 ? amountDueNow : total)}`,
    ]
  } else if (mode === 'NONE') {
    // Phase A : NONE + carte = paiement intégral volontaire (pas des arrhes)
    // modePaiement choisi prime sur un PI abandonné (AWAITING_PAYMENT + surplace).
    const onsiteChosen = modePaiement === 'surplace';
    if (
      !onsiteChosen &&
      (modePaiement === 'enligne' || amountDueNow > 0 || paymentStatus === 'AWAITING_PAYMENT')
    ) {
      modeLabel = 'Carte bancaire (paiement intégral)'
      if (amountPaid >= total && total > 0) summaryLabel = 'Payé intégralement'
      else if (paymentStatus === 'AWAITING_PAYMENT') summaryLabel = 'Paiement intégral en ligne'
      else if (amountPaid > 0) summaryLabel = 'Partiellement payé'
      else summaryLabel = 'Paiement intégral en ligne'
      detailLines = [
        'Paiement : Carte bancaire',
        amountPaid > 0
          ? `Payé en ligne : ${formatEuro(amountPaid)}`
          : `À payer en ligne : ${formatEuro(amountDueNow > 0 ? amountDueNow : total)}`,
      ]
      if (amountDueLater > 0) {
        detailLines.push(`Reste à payer : ${formatEuro(amountDueLater)}`)
      }
    } else {
      modeLabel = 'Sur place'
      summaryLabel = amountPaid > 0 ? 'Partiellement réglé' : 'À régler sur place'
      detailLines = [
        'Paiement : Sur place',
        `À payer sur place : ${formatEuro(amountDueLater > 0 ? amountDueLater : total)}`,
      ]
    }
  }

  return {
    mode,
    modeLabel,
    summaryLabel,
    detailLines,
    total,
    amountPaid,
    amountDueNow,
    amountDueLater,
    paymentStatus,
    modePaiement: modePaiement || null,
    isOnline:
      mode === 'DEPOSIT_FIXED' ||
      mode === 'FULL' ||
      modePaiement === 'enligne' ||
      amountDueNow > 0,
    isRefunded: paymentStatus === 'REFUNDED',
  }
}

/**
 * Solde Stripe encore remboursable pour un RDV (net snapshot / ledger).
 */
export function getRdvSoldeRemboursable(commande) {
  const view = getRdvPaymentView(commande)
  if (!view) return 0
  if (view.isRefunded) return 0
  const paid = Number(view.amountPaid)
  return Number.isFinite(paid) && paid > 0 ? Math.round(paid * 100) / 100 : 0
}

/**
 * Éligibilité bouton / modal « Rembourser » RDV Admin.
 */
export function canRefundRendezVous(commande) {
  if (!commande || (commande.type !== 'RENDEZ_VOUS' && !String(commande.numero || '').startsWith('RDV-'))) {
    return false
  }
  const view = getRdvPaymentView(commande)
  if (!view) return false
  if (view.isRefunded) return false
  if (!(view.amountPaid > 0)) return false

  const piStatut = String(
    commande.paiementIntentStatut || commande.paiementIntent?.statut || ''
  ).toUpperCase()
  if (piStatut === 'REMBOURSE') return false
  // Capture Stripe connue (PI REUSSI) ou ledger net > 0 déjà vérifié
  if (piStatut && piStatut !== 'REUSSI' && !(view.amountPaid > 0)) return false

  // Sur place sans encaissement Stripe : amountPaid = 0 déjà filtré
  if (!view.isOnline && !(view.amountPaid > 0)) return false

  return getRdvSoldeRemboursable(commande) > 0
}
